import {Component, computed, effect, inject, signal} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {KEYCLOAK_EVENT_SIGNAL, KeycloakEventType, ReadyArgs, typeEventArgs} from 'keycloak-angular';
import {Button} from 'primeng/button';
import {TranslocoPipe, TranslocoService} from '@jsverse/transloco';
import {MenuItem} from 'primeng/api';
import {Menubar} from 'primeng/menubar';
import {RouterLink} from '@angular/router';
import {AuthService} from '../../../shared/services/auth.service';
import {LanguageDropdownComponent} from '../../../shared/components/inputs/language-dropdown/language-dropdown.component';
import {lastValueFrom} from 'rxjs';
import {RoleCheckService} from '../../../shared/services/role-check.service';
import {UserProfileService} from '../../../shared/services/user-profile.service';
import {APP_ROUTES} from '../../../app.routes';
import {ChatBotStateService} from '../../../shared/chatbot/ChatBotStateService';

@Component({
  selector: 'app-user-context-menu',
  imports: [Button, TranslocoPipe, Menubar, RouterLink, LanguageDropdownComponent],
  templateUrl: './user-context-menu.component.html',
})
export class UserContextMenuComponent {
  authenticated = signal(false);
  private readonly currentLang = signal<string>('');

  private readonly keycloakSignal = inject(KEYCLOAK_EVENT_SIGNAL);
  protected readonly authService = inject(AuthService);
  private readonly translocoService = inject(TranslocoService);
  private readonly roleCheckService = inject(RoleCheckService);
  private readonly userProfileService = inject(UserProfileService);
  private readonly chatStateService = inject(ChatBotStateService);
  private readonly activeLang = toSignal(this.translocoService.langChanges$, {initialValue: ''});

  protected readonly user = this.userProfileService.currentUserProfile;

  items = computed(() => {
    const isAuth = this.authenticated();
    const user = this.user();
    const lang = this.currentLang();

    if (!isAuth || !user || !lang) {
      return [];
    }

    return this.generateMenuItems();
  });

  constructor() {
    effect(async () => {
      const keycloakEvent = this.keycloakSignal();

      if (keycloakEvent.type === KeycloakEventType.Ready) {
        this.authenticated.set(typeEventArgs<ReadyArgs>(keycloakEvent.args));
        await this.userProfileService.loadUserProfileOnAppStart();

        const currentLang = this.translocoService.getActiveLang();
        await lastValueFrom(this.translocoService.load(currentLang));
        this.currentLang.set(currentLang);
      }

      if (keycloakEvent.type === KeycloakEventType.AuthLogout) {
        this.authenticated.set(false);
        this.userProfileService.resetUserProfileOnLogout();
        this.currentLang.set('');
      }
    });

    effect(async () => {
      const lang = this.activeLang();
      if (lang && lang !== this.currentLang() && this.authenticated()) {
        await lastValueFrom(this.translocoService.load(lang));
        this.currentLang.set(lang);
      }
    });
  }

  private generateMenuItems(): MenuItem[] {
    const menuItems: MenuItem[] = [];

    if (!this.roleCheckService.hasSelecthorAdminRole()) {
      menuItems.push({
        label: this.translocoService.translate('user.menu.userProfile'),
        icon: 'pi pi-user',
        routerLink: [APP_ROUTES.USER_PROFILE],
      });
    }

    menuItems.push({
      label: this.translocoService.translate('user.menu.logout'),
      icon: 'pi pi-sign-out',
      command: () => {
        this.logout();
      },
    });

    return [
      {
        label: this.user()?.name,
        items: menuItems,
      },
    ];
  }

  async login() {
    await this.authService.login(APP_ROUTES.PROJECT);
  }

  logout(): void {
    this.chatStateService.clear();
    this.authService.logout();
  }

  protected readonly APP_ROUTES = APP_ROUTES;
}
